import React, { useEffect, useState } from 'react';
import {
    Search, Plus, CheckCircle, Clock, Eye, AlertTriangle,
    ChevronLeft, ChevronRight, ExternalLink, Bell, Copy,
    Building2, Calendar, MessageSquare, MoreVertical, X,
    FileText, Link2, Trash2, RefreshCw, History
} from 'lucide-react';
import { toast } from 'sonner';
import { useBrandApprovalStore } from '../../store/brandApprovalStore';
import { BrandApproval } from '../../services/brandApproval.service';

const BrandApprovalsPage: React.FC = () => {
    const {
        approvals,
        stats,
        isLoading,
        activeTab,
        searchTerm,
        selectedApproval,
        auditLog,
        fetchApprovals,
        fetchStats,
        fetchAuditLog,
        setActiveTab,
        setSearchTerm,
        setSelectedApproval,
        deleteApproval,
        sendReminder,
        regeneratePortalLink,
        getFilteredApprovals,
    } = useBrandApprovalStore();

    // Modal states
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    // Pagination
    const ITEMS_PER_PAGE = 9;
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        fetchApprovals();
        fetchStats();
    }, [fetchApprovals, fetchStats]);

    // Reset page when tab or search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm]);

    const filteredApprovals = getFilteredApprovals();
    const paginatedApprovals = filteredApprovals.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );
    const totalPages = Math.ceil(filteredApprovals.length / ITEMS_PER_PAGE);

    const handleViewApproval = async (approval: BrandApproval) => {
        setSelectedApproval(approval);
        await fetchAuditLog(approval.id);
        setIsDetailModalOpen(true);
    };

    const handleDeleteApproval = async (approval: BrandApproval) => {
        if (window.confirm(`Delete approval "${approval.title}"?`)) {
            await deleteApproval(approval.id);
        }
    };

    const handleCopyPortalLink = (link: string) => {
        navigator.clipboard.writeText(link);
        toast.success('Portal link copied to clipboard');
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending_brand_review':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300">
                        <Clock className="w-3 h-3" />
                        Awaiting Brand
                    </span>
                );
            case 'in_brand_review':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        <Eye className="w-3 h-3" />
                        In Review
                    </span>
                );
            case 'approved':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                        <CheckCircle className="w-3 h-3" />
                        Approved
                    </span>
                );
            case 'approved_with_changes':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                        <CheckCircle className="w-3 h-3" />
                        Approved (Changes)
                    </span>
                );
            case 'rejected':
                return (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                        <AlertTriangle className="w-3 h-3" />
                        Rejected
                    </span>
                );
            default:
                return null;
        }
    };

    if (isLoading && approvals.length === 0) {
        return (
            <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Brand Approvals</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Manage client reviews and approvals</p>
                </div>

                {/* Stats Overview */}
                <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Awaiting</div>
                        <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-yellow-500" />
                            <span className="text-xl font-bold text-yellow-600">{stats?.pendingBrandReview || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">In Review</div>
                        <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4 text-blue-500" />
                            <span className="text-xl font-bold text-blue-600">{stats?.inBrandReview || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Approved</div>
                        <div className="flex items-center gap-1">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-xl font-bold text-green-600">{stats?.approved || 0}</span>
                        </div>
                    </div>
                    <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-1 min-w-0">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Changes</div>
                        <div className="flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4 text-orange-500" />
                            <span className="text-xl font-bold text-orange-600">{stats?.pendingChangeRequests || 0}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tab Navigation and Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Tabs */}
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                        {[
                            { key: 'pending', label: 'Awaiting', count: stats?.pendingBrandReview || 0 },
                            { key: 'in_review', label: 'In Review', count: stats?.inBrandReview || 0 },
                            { key: 'approved', label: 'Approved', count: stats?.approved || 0 },
                            { key: 'all', label: 'All', count: null },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => { setActiveTab(tab.key as any); setCurrentPage(1); }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab.key
                                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                    }`}
                            >
                                {tab.label}
                                {tab.count !== null && tab.count > 0 && (
                                    <span className="px-1.5 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="flex flex-col sm:flex-row gap-4 flex-1 max-w-xl">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search brand approvals..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                            />
                        </div>
                    </div>

                    {/* Action Button */}
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        <Plus className="w-4 h-4" />
                        Submit for Review
                    </button>
                </div>
            </div>

            {/* Content */}
            {filteredApprovals.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-indigo-100 dark:from-primary-900/30 dark:to-indigo-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Building2 className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        No brand approvals yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                        When you submit content for brand review, it will appear here.
                    </p>
                    <button
                        onClick={() => setIsCreateModalOpen(true)}
                        className="px-6 py-3 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                        Submit for Brand Review
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {paginatedApprovals.map((approval) => (
                            <BrandApprovalCard
                                key={approval.id}
                                approval={approval}
                                onView={() => handleViewApproval(approval)}
                                onDelete={() => handleDeleteApproval(approval)}
                                onSendReminder={() => sendReminder(approval.id)}
                                onCopyLink={() => approval.portalLink && handleCopyPortalLink(approval.portalLink)}
                                getStatusBadge={getStatusBadge}
                            />
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                                Page {currentPage} of {totalPages} ({filteredApprovals.length} items)
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                </button>
                                <button
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Detail Modal */}
            <BrandApprovalDetailModal
                isOpen={isDetailModalOpen}
                onClose={() => {
                    setIsDetailModalOpen(false);
                    setSelectedApproval(null);
                }}
                approval={selectedApproval}
                auditLog={auditLog}
                onRegenerateLink={() => selectedApproval && regeneratePortalLink(selectedApproval.id)}
                onSendReminder={() => selectedApproval && sendReminder(selectedApproval.id)}
                getStatusBadge={getStatusBadge}
            />

            {/* Create Modal */}
            <SubmitForBrandReviewModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
            />
        </div>
    );
};

// ============ BrandApprovalCard Component ============
interface BrandApprovalCardProps {
    approval: BrandApproval;
    onView: () => void;
    onDelete: () => void;
    onSendReminder: () => void;
    onCopyLink: () => void;
    getStatusBadge: (status: string) => React.ReactNode;
}

const BrandApprovalCard: React.FC<BrandApprovalCardProps> = ({
    approval,
    onView,
    onDelete,
    onSendReminder,
    onCopyLink,
    getStatusBadge,
}) => {
    const [showMenu, setShowMenu] = useState(false);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-5 hover:shadow-lg transition-all duration-200">
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {approval.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        v{approval.versionNumber} • Round {approval.currentRound}
                    </p>
                </div>
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <MoreVertical className="w-4 h-4 text-gray-400" />
                    </button>
                    {showMenu && (
                        <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
                            <button
                                onClick={() => { onView(); setShowMenu(false); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                                <Eye className="w-4 h-4" /> View Details
                            </button>
                            {approval.portalLink && (
                                <button
                                    onClick={() => { onCopyLink(); setShowMenu(false); }}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                >
                                    <Copy className="w-4 h-4" /> Copy Portal Link
                                </button>
                            )}
                            <button
                                onClick={() => { onSendReminder(); setShowMenu(false); }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                                <Bell className="w-4 h-4" /> Send Reminder
                            </button>
                            <hr className="my-1 border-gray-200 dark:border-gray-700" />
                            <button
                                onClick={() => { onDelete(); setShowMenu(false); }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                            >
                                <Trash2 className="w-4 h-4" /> Delete
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Brand Contact */}
            <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-300">
                    {approval.brandContact.company}
                </span>
                <span className="text-gray-400 dark:text-gray-500">•</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                    {approval.brandContact.name}
                </span>
            </div>

            {/* Status */}
            <div className="mb-3">
                {getStatusBadge(approval.status)}
            </div>

            {/* Meta Info */}
            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {new Date(approval.submittedAt).toLocaleDateString()}
                </span>
                {approval.changeRequests.length > 0 && (
                    <span className="flex items-center gap-1">
                        <MessageSquare className="w-3.5 h-3.5" />
                        {approval.changeRequests.filter(cr => cr.status === 'pending').length} pending
                    </span>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
                <button
                    onClick={onView}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    <Eye className="w-4 h-4" />
                    View
                </button>
                {approval.portalLink && (
                    <button
                        onClick={() => window.open(approval.portalLink, '_blank')}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/30 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                    >
                        <ExternalLink className="w-4 h-4" />
                        Portal
                    </button>
                )}
                <button
                    onClick={onSendReminder}
                    className="flex items-center justify-center p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Send Reminder"
                >
                    <Bell className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// ============ BrandApprovalDetailModal Component ============
interface BrandApprovalDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    approval: BrandApproval | null;
    auditLog: any[];
    onRegenerateLink: () => void;
    onSendReminder: () => void;
    getStatusBadge: (status: string) => React.ReactNode;
}

const BrandApprovalDetailModal: React.FC<BrandApprovalDetailModalProps> = ({
    isOpen,
    onClose,
    approval,
    auditLog,
    onRegenerateLink,
    onSendReminder,
    getStatusBadge,
}) => {
    const [activeTab, setActiveTab] = useState<'details' | 'changes' | 'history'>('details');

    if (!isOpen || !approval) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{approval.title}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            v{approval.versionNumber} • Round {approval.currentRound} • {approval.brandContact.company}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {getStatusBadge(approval.status)}
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
                    {[
                        { key: 'details', label: 'Details', icon: FileText },
                        { key: 'changes', label: 'Change Requests', icon: MessageSquare, count: approval.changeRequests.length },
                        { key: 'history', label: 'Audit Log', icon: History, count: auditLog.length },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key as any)}
                            className={`flex items-center gap-2 px-4 py-3 border-b-2 text-sm font-medium transition-colors ${activeTab === tab.key
                                ? 'border-primary-600 text-primary-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                            {tab.count !== undefined && tab.count > 0 && (
                                <span className="px-1.5 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded-full">
                                    {tab.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {activeTab === 'details' && (
                        <div className="space-y-6">
                            {/* Brand Contact */}
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Brand Contact</h3>
                                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                                    <p className="font-medium text-gray-900 dark:text-white">{approval.brandContact.name}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">{approval.brandContact.email}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">{approval.brandContact.company}</p>
                                </div>
                            </div>

                            {/* Portal Link */}
                            {approval.portalLink && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Portal Link</h3>
                                    <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                                        <Link2 className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-600 dark:text-gray-300 truncate flex-1">
                                            {approval.portalLink}
                                        </span>
                                        <button
                                            onClick={() => navigator.clipboard.writeText(approval.portalLink!)}
                                            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                        >
                                            <Copy className="w-4 h-4 text-gray-500" />
                                        </button>
                                        <button
                                            onClick={onRegenerateLink}
                                            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                            title="Regenerate Link"
                                        >
                                            <RefreshCw className="w-4 h-4 text-gray-500" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Accessed {approval.portalAccessCount} times • Expires {approval.portalExpiresAt ? new Date(approval.portalExpiresAt).toLocaleDateString() : 'N/A'}
                                    </p>
                                </div>
                            )}

                            {/* Description */}
                            {approval.description && (
                                <div>
                                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Description</h3>
                                    <p className="text-gray-700 dark:text-gray-300">{approval.description}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'changes' && (
                        <div className="space-y-4">
                            {approval.changeRequests.length === 0 ? (
                                <p className="text-center text-gray-500 py-8">No change requests yet</p>
                            ) : (
                                approval.changeRequests.map((cr) => (
                                    <div key={cr.requestId} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <span className="text-xs font-mono text-gray-500">{cr.requestId}</span>
                                                <h4 className="font-medium text-gray-900 dark:text-white">{cr.title}</h4>
                                            </div>
                                            <span className={`px-2 py-1 text-xs rounded-full ${cr.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                cr.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                                    'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                {cr.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{cr.description}</p>
                                        <div className="flex items-center gap-4 text-xs text-gray-500">
                                            <span>Priority: {cr.priority}</span>
                                            <span>Category: {cr.category}</span>
                                            {cr.estimatedEffort && <span>Effort: {cr.estimatedEffort}</span>}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="space-y-3">
                            {auditLog.length === 0 ? (
                                <p className="text-center text-gray-500 py-8">No audit log entries</p>
                            ) : (
                                auditLog.map((entry, idx) => (
                                    <div key={idx} className="flex items-start gap-3 py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${entry.actorType === 'brand' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                                            }`}>
                                            {entry.actor.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-900 dark:text-white">
                                                <span className="font-medium">{entry.actor.name}</span>
                                                {' '}{entry.action.replace(/_/g, ' ')}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {new Date(entry.createdAt).toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={onSendReminder}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        <Bell className="w-4 h-4" />
                        Send Reminder
                    </button>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============ SubmitForBrandReviewModal Component ============
interface SubmitForBrandReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const SubmitForBrandReviewModal: React.FC<SubmitForBrandReviewModalProps> = ({ isOpen, onClose }) => {
    const { createApproval } = useBrandApprovalStore();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        projectId: '',
        brandContactName: '',
        brandContactEmail: '',
        brandContactCompany: '',
        previewUrl: '',
        reviewSLA: 48,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title || !formData.projectId || !formData.brandContactName || !formData.brandContactEmail || !formData.brandContactCompany) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSubmitting(true);
        try {
            const approvalData: any = {
                title: formData.title,
                description: formData.description,
                projectId: formData.projectId,
                brandContact: {
                    name: formData.brandContactName,
                    email: formData.brandContactEmail,
                    company: formData.brandContactCompany,
                },
                reviewSLA: formData.reviewSLA,
            };
            if (formData.previewUrl) {
                approvalData.previewUrl = formData.previewUrl;
            }
            await createApproval(approvalData);
            onClose();
            setFormData({
                title: '',
                description: '',
                projectId: '',
                brandContactName: '',
                brandContactEmail: '',
                brandContactCompany: '',
                previewUrl: '',
                reviewSLA: 48,
            });
        } catch (error) {
            // Error already handled in store
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Submit for Brand Review</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Content title for review"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project ID *</label>
                        <input
                            type="text"
                            value={formData.projectId}
                            onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Associated project ID"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand Contact Name *</label>
                            <input
                                type="text"
                                value={formData.brandContactName}
                                onChange={(e) => setFormData({ ...formData, brandContactName: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand Company *</label>
                            <input
                                type="text"
                                value={formData.brandContactCompany}
                                onChange={(e) => setFormData({ ...formData, brandContactCompany: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brand Contact Email *</label>
                        <input
                            type="email"
                            value={formData.brandContactEmail}
                            onChange={(e) => setFormData({ ...formData, brandContactEmail: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview URL</label>
                        <input
                            type="url"
                            value={formData.previewUrl}
                            onChange={(e) => setFormData({ ...formData, previewUrl: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="https://..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Review SLA (hours)</label>
                        <select
                            value={formData.reviewSLA}
                            onChange={(e) => setFormData({ ...formData, reviewSLA: parseInt(e.target.value) })}
                            className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                            <option value={24}>24 hours</option>
                            <option value={48}>48 hours</option>
                            <option value={72}>72 hours</option>
                            <option value={168}>1 week</option>
                        </select>
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit for Review'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BrandApprovalsPage;
